<template>
    <AppModal :title="$t('editProductNameModal.edit_product_name')" ref="modal" size="full" @on-hide="hide">
        <div class="modal-body">
            <template v-if="task.state.isRunning">
                <TaskProgressBar :done="task.state.done" :total="task.state.total"/>
            </template>
            <template v-else>
                <div class="row">
                    <div class="col-lg-9 col-md-8 col-8">
                        <AppToast :title="$t('labels.publications')">
                            <div class="toast-body">
                                <div class="table-responsive">
                                    <table class="table table-bordered table-fixed">
                                        <thead>
                                            <tr>
                                                <th width="100px" class="text-overflow">{{ $t('table.titles.id') }}</th>
                                                <th width="100px" class="text-overflow">{{ $t('table.titles.sku') }}</th>
                                                <th width="78px"  class="text-overflow">{{ $t('table.titles.image') }}</th>
                                                <th style="min-width: 300px" class="text-overflow">{{ $t('table.titles.name') + ` (${$t(`shortLang.uk`)})` }}</th>
                                                <th style="min-width: 300px" class="text-overflow">{{ $t('table.titles.name') + ` (${$t(`shortLang.ru`)})` }}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="product in products">
                                                <td>
                                                    <router-link :to="{name: 'Product', params: { integrationProductUuid: product.uuid }}">{{ product.id }}</router-link>
                                                </td>
                                                <td>
                                                    <AppNullableCell>
                                                        <template #default v-if="product.sku">
                                                            <span class="long-string" :title="product.sku">{{ product.sku }}</span>
                                                        </template>
                                                    </AppNullableCell>
                                                </td>
                                                <td>
                                                    <img
                                                        class="table-image"
                                                        :src="product.image ?? PLACEHOLDER_IMAGE_URL"
                                                        :alt="getAvailableTranslations(product.oldNames)"
                                                    />
                                                </td>
                                                <td>
                                                    <AppNullableCell>
                                                        <template #default v-if="product.oldNames['uk']">
                                                            <div v-if="processName(product, 'uk').hasChanges">
                                                                <span class="long-string" :title="processName(product, 'uk').newName">
                                                                    {{ processName(product, 'uk').newName }}
                                                                </span>
                                                                <span class="long-string text-muted" :title="product.oldNames['uk']" v-html="processName(product, 'uk').highlightedName"></span>
                                                            </div>
                                                            <span v-else class="long-string" :title="product.oldNames['uk']">{{ product.oldNames['uk'] }}</span>
                                                        </template>
                                                    </AppNullableCell>
                                                </td>
                                                <td>
                                                    <AppNullableCell>
                                                        <template #default v-if="product.oldNames['ru']">
                                                            <div v-if="processName(product, 'ru').hasChanges">
                                                                <span class="long-string" :title="processName(product, 'ru').newName">
                                                                    {{ processName(product, 'ru').newName }}
                                                                </span>
                                                                <span class="long-string text-muted" :title="product.oldNames['ru']" v-html="processName(product, 'ru').highlightedName"></span>
                                                            </div>
                                                            <span v-else class="long-string" :title="product.oldNames['ru']">{{ product.oldNames['ru'] }}</span>
                                                        </template>
                                                    </AppNullableCell>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </AppToast>
                    </div>
                    <div class="col-lg-3 col-md-4 col-3 sidebar-sticky">
                        <AppToast :title="$t('editProductNameModal.remove')">
                            <div class="toast-body">
                                <InputGroup class="mb-3">
                                    <Input v-model="newRemoveFromNameValue" @keydown:enter="addNewRemoveFromNameValue()"/>
                                    <template #append>
                                        <AppButton color="teal" :disabled="!newRemoveFromNameValue || !hasMatch(newRemoveFromNameValue)" @click="addNewRemoveFromNameValue()">
                                            <i class="icon-plus3"></i>
                                        </AppButton>
                                    </template>
                                </InputGroup>
                                <InputGroup v-for="valueForRemoveFromNames in valuesForRemoveFromNames" class="mb-3">
                                    <Input :model-value="valueForRemoveFromNames" readonly/>
                                    <template #append>
                                        <AppButton color="light" @click="removeNewRemoveFromNameValue(valueForRemoveFromNames)">
                                            <i class="icon-trash"></i>
                                        </AppButton>
                                    </template>
                                </InputGroup>
                            </div>
                        </AppToast>
                        <AppToast :title="$t('editProductNameModal.add')">
                            <div class="toast-body">
                                <div class="row">
                                    <div class="col-6">
                                        <FormGroup :label="$t('labels.type')">
                                            <AppSelect
                                                @update:model-value="addType = $event"
                                                :model-value="addType.key"
                                                :selected-value="addType.name"
                                                :options="addTypeOptions"
                                            />
                                        </FormGroup>
                                    </div>
                                    <div class="col-6">
                                        <FormGroup :label="$t('labels.position')">
                                            <AppSelect
                                                @update:model-value="addPosition = $event"
                                                :model-value="addPosition.key"
                                                :selected-value="addPosition.name"
                                                :options="addPositionOptions"
                                            />
                                        </FormGroup>
                                    </div>
                                    <div class="col-12" v-if="addType.key === 'filter'">
                                        <FormGroup :label="$t('labels.first_letter_case')">
                                            <AppSelect
                                                @update:model-value="textCaseMode = $event"
                                                :model-value="textCaseMode?.key"
                                                :selected-value="textCaseMode?.name"
                                                :options="textCaseModeOptions"
                                                can-be-empty
                                            />
                                        </FormGroup>
                                    </div>
                                </div>
                                <FormGroup :label="$t('labels.value')" v-if="addType.key !== 'category' && addType.key !== 'sku'">
                                    <AppAutoComplete
                                        v-if="addType.key === 'filter'"
                                        @update:model-value="newAddToNameValue = $event"
                                        :query-params="{ integration_category_uuids: [$route.params.categoryUuid] }"
                                        :model-value="newAddToNameValue?.uuid"
                                        :selected-value="newAddToNameValue?.name"
                                        :selected-values="[...valuesForAddToNamesStart, ...valuesForAddToNamesEnd].filter(value => value.type === 'filter').map(value => value.value.uuid)"
                                        :url="`/projects/${$route.params.projectUuid}/pim/channels/${$route.params.integrationUuid}/filters`"
                                        can-be-empty
                                    />
                                    <div v-else-if="addType.key === 'text'" class="d-flex align-items-center" :style="{gap: '.5rem'}" >
                                        <div class="flex-grow-1 d-flex flex-column" :style="{gap: '.5rem'}">
                                            <InputGroup v-for="language in integration.language_aliases">
                                                <template #prepend>
                                                    <span>{{ language.toUpperCase() }}</span>
                                                </template>
                                                <Input v-model="newAddToNameTextValue[language]" />
                                            </InputGroup>
                                        </div>
                                    </div>
                                </FormGroup>
                                <div class="d-flex justify-content-end mb-3">
                                    <AppButton color="teal" :disabled="isAddButtonDisabled()" @click="addNewAddToNameValue()">
                                        <i class="icon-plus3"></i>
                                    </AppButton>
                                </div>
                                <AppFieldset v-if="valuesForAddToNamesStart.length > 0" :title="$t('labels.position_start')" class="mb-3">
                                    <draggable v-model="valuesForAddToNamesStart" handle=".handle">
                                        <div v-for="(item, index) in valuesForAddToNamesStart" :key="item.id" class="mb-2">
                                            <div class="d-flex justify-content-between align-items-center mb-2">
                                                <span class="handle">
                                                    <i class="icon-dots dragula-handle mr-2"></i>
                                                    {{ $t(`labels.${item.type}`) }}
                                                </span>
                                                <AppButton color="light" @click="removeNewAddToNameValue(index, 'start')">
                                                    <i class="icon-trash"></i>
                                                </AppButton>
                                            </div>
                                            <div class="d-flex flex-wrap justify-content-end" :style="{gap: '.5rem'}" v-if="item.type === 'text'">
                                                <InputGroup v-for="language in integration.language_aliases" style="width: 100%">
                                                    <template #prepend>
                                                        <span>{{ language.toUpperCase() }}</span>
                                                    </template>
                                                    <Input :model-value="item.value[language]" readonly/>
                                                </InputGroup>
                                            </div>
                                            <InputGroup class="mb-3" v-else>
                                                <Input :model-value="['category', 'sku'].includes(item.type) ? $t(item.type === 'category' ? 'labels.category' : 'table.titles.sku') : getAvailableTranslations(item.type === 'filter' ? item.value.names : item.value)" readonly/>
                                            </InputGroup>
                                        </div>
                                    </draggable>
                                </AppFieldset>
                                <AppFieldset v-if="valuesForAddToNamesEnd.length > 0" :title="$t('labels.position_end')" class="mb-3">
                                    <draggable v-model="valuesForAddToNamesEnd" handle=".handle">
                                        <div v-for="(item, index) in valuesForAddToNamesEnd" :key="item.id" class="mb-2">
                                            <div class="d-flex justify-content-between align-items-center mb-2">
                                                <span class="handle">
                                                    <i class="icon-dots dragula-handle mr-2"></i>
                                                    {{ $t(`labels.${item.type}`) }}
                                                </span>
                                                <AppButton color="light" @click="removeNewAddToNameValue(index, 'end')">
                                                    <i class="icon-trash"></i>
                                                </AppButton>
                                            </div>
                                            <div class="d-flex flex-wrap justify-content-end" :style="{gap: '.5rem'}" v-if="item.type === 'text'">
                                                <InputGroup v-for="language in integration.language_aliases" style="width: 100%">
                                                    <template #prepend>
                                                        <span>{{ language.toUpperCase() }}</span>
                                                    </template>
                                                    <Input :model-value="item.value[language]" readonly/>
                                                </InputGroup>
                                            </div>
                                            <InputGroup class="mb-3" v-else>
                                                <Input :model-value="['category', 'sku'].includes(item.type) ? $t(item.type === 'category' ? 'labels.category' : 'table.titles.sku') : getAvailableTranslations(item.type === 'filter' ? item.value.names : item.value)" readonly/>
                                            </InputGroup>
                                        </div>
                                    </draggable>
                                </AppFieldset>
                            </div>
                        </AppToast>
                    </div>
                </div>
            </template>
        </div>
        <div class="modal-footer" v-if="!task.state.isRunning">
            <AppButton color="indigo" @click="saveNewNames">
                {{ $t("buttons.save") }}
            </AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from "../../../../../../components/new/modal/AppModal.vue";
import AppFieldset from "../../../../../../components/new/AppFieldset.vue";
import AppNullableCell from "../../../../../components/tables/AppNullableCell.vue";
import AppButton from "../../../../../../components/new/button/AppButton.vue";
import InputGroup from "../../../../../../components/new/form/InputGroup.vue";
import Input from "../../../../../../components/new/form/Input.vue";
import AppAutoComplete from "../../../../../../components/new/form/autocomplete/AppAutoComplete.vue";
import AppSelect from "../../../../../../components/new/form/select/AppSelect.vue";
import TaskProgressBar from "../../../../../../components/new/progress/TaskProgressBar.vue";
import AppToast from "../../../../../../components/new/AppToast.vue";
import FormGroup from "../../../../../../components/new/form/group/FormGroup.vue";

import { PLACEHOLDER_IMAGE_URL } from "../../../../../../constants";
import { httpClient } from "../../../../../../services/HttpClient";
import HttpRequest from "../../../../../../services/Http/HttpRequest";
import HttpSender from "../../../../../../services/Http/HttpSender";
import { createProgressTask } from "../../../../../../services/Progress/ProgressTask";
import { VueDraggableNext } from 'vue-draggable-next';

export default {
    name: "EditProductNameModal",
    components: {
        AppModal, AppFieldset, AppNullableCell, AppButton, InputGroup, Input, AppAutoComplete, AppSelect, TaskProgressBar, AppToast,
        FormGroup, draggable: VueDraggableNext
    },
    emits: ['names:updated'],
    props: {
        getAvailableTranslations: {
            type: Function,
            required: true
        },
        integration: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            PLACEHOLDER_IMAGE_URL,
            selectedProductUuids: [],
            products: [],
            newRemoveFromNameValue: '',
            newAddToNameValue: null,
            addType: { key: 'text', name: this.$t('labels.text') },
            addPosition: { key: 'start', name: this.$t('labels.position_start') },
            newAddToNameTextValue: {},
            valuesForRemoveFromNames: [],
            valuesForAddToNamesStart: [],
            valuesForAddToNamesEnd: [],
            task: createProgressTask(),
            addTypeOptions: [
                { key: 'category', name: this.$t('labels.category') },
                { key: 'sku', name: this.$t('table.titles.sku') },
                { key: 'filter', name: this.$t('labels.filter') },
                { key: 'text', name: this.$t('labels.text') }
            ],
            addPositionOptions: [
                { key: 'start', name: this.$t('labels.position_start') },
                { key: 'end', name: this.$t('labels.position_end') }
            ],
            textCaseMode: null,
            textCaseModeOptions: [
                { key: 'upper', name: this.$t('editProductNameModal.uppercase') },
                { key: 'lower', name: this.$t('editProductNameModal.lowercase') }
            ]
        }
    },
    methods: {
        show(selectedProductUuids) {
            this.selectedProductUuids = selectedProductUuids;
            
            this.$refs.modal.show();

            this.$nextTick(async () => {
                this.products = await this.fetchTable(selectedProductUuids);
            })
        },
        hide() {
            this.task.cancel();
            this.$refs.modal.hide();
            this.newRemoveFromNameValue = '';
            this.newAddToNameValue = null;
            this.newAddToNameTextValue = {};
            this.addType = { key: 'text', name: this.$t('labels.text') };
            this.addPosition = { key: 'start', name: this.$t('labels.position_start') };
            this.valuesForRemoveFromNames = [];
            this.valuesForAddToNamesStart = [];
            this.valuesForAddToNamesEnd = [];
            this.products = [];
            this.selectedProductUuids = [];
        },
        async fetchTable(selectedProductUuids) {
            return await httpClient.get(`/integrations/${this.$route.params.integrationUuid}/products`, {
                limit: 100,
                uuids: selectedProductUuids,
                extra_fields: ['integrationFilterItems'],
            }).then((response) => {
                return response.data.data.map(product => ({
                    id: product.id,
                    sku: product.sku,
                    image: product.picture_url,
                    uuid: product.uuid,
                    oldNames: product.names || {},
                    newNames: {},
                    categoryNames: product.category.names,
                    filterItemNamesByFilterUuid: product.integration_filter_items.reduce((acc, item) => {
                        acc[item.integration_filter_uuid] = item.names;
                        return acc;
                    }, {})
                }));
            });
        },
        hasMatch(value) {
            return this.products.some(product => {
                return Object.values(product.oldNames).some(name => name.includes(value));
            })
        },
        addNewRemoveFromNameValue() {
            if (this.valuesForRemoveFromNames.includes(this.newRemoveFromNameValue) || !this.hasMatch(this.newRemoveFromNameValue)) {
                return;
            }

            this.valuesForRemoveFromNames.push(this.newRemoveFromNameValue);
            this.newRemoveFromNameValue = '';
        },
        addNewAddToNameValue() {
            if (this.addType.key === 'filter') {
                if (this.newAddToNameValue) {
                    const item = {
                        id: Date.now() + Math.random(),
                        type: 'filter',
                        position: this.addPosition.key,
                        caseMode: this.textCaseMode,
                        value: {
                            uuid: this.newAddToNameValue.uuid,
                            names: this.newAddToNameValue.names
                        }
                    };

                    if (this.addPosition.key === 'start') {
                        this.valuesForAddToNamesStart.push(item);
                    } else {
                        this.valuesForAddToNamesEnd.push(item);
                    }
                    this.newAddToNameValue = null;
                }
            } else if (this.addType.key === 'text') {
                if (this.newAddToNameTextValue) {
                    let value = {};

                    Object.keys(this.newAddToNameTextValue).forEach(key => {
                        let text = this.newAddToNameTextValue[key];

                        if (text) {
                            value[key] = text;
                        }
                    });

                    const item = {
                        id: Date.now() + Math.random(),
                        type: 'text',
                        position: this.addPosition.key,
                        value: value
                    };

                    if (this.addPosition.key === 'start') {
                        this.valuesForAddToNamesStart.push(item);
                    } else {
                        this.valuesForAddToNamesEnd.push(item);
                    }
                    this.newAddToNameTextValue = {};
                }
            } else if (['category', 'sku'].includes(this.addType.key)) {
                const item = {
                    id: Date.now() + Math.random(),
                    type: this.addType.key,
                    position: this.addPosition.key,
                    value: null
                };

                if (this.addPosition.key === 'start') {
                    this.valuesForAddToNamesStart.push(item);
                } else {
                    this.valuesForAddToNamesEnd.push(item);
                }
            }
        },
        removeNewRemoveFromNameValue(valueForRemoveFromNames) {
            this.valuesForRemoveFromNames = this.valuesForRemoveFromNames.filter(value => value !== valueForRemoveFromNames);
        },
        removeNewAddToNameValue(index, type) {
            if (type === 'start') {
                this.valuesForAddToNamesStart.splice(index, 1);
            } else {
                this.valuesForAddToNamesEnd.splice(index, 1);
            }
        },
        processName(product, lang) {
            const name = product.oldNames[lang];
            
            if (!name) {
                return { hasChanges: false, newName: '', highlightedName: '' };
            }

            let parts = [{ text: name, highlight: false }];

            if (this.valuesForRemoveFromNames.length > 0) {
                this.valuesForRemoveFromNames.forEach(value => {
                    if (!value) return;

                    let newParts = [];
                    parts.forEach(part => {
                        if (part.highlight) {
                            newParts.push(part);
                        } else {
                            const split = part.text.split(value);
                            for (let i = 0; i < split.length; i++) {
                                if (split[i]) {
                                    newParts.push({ text: split[i], highlight: false });
                                }
                                if (i < split.length - 1) {
                                    newParts.push({ text: value, highlight: true });
                                }
                            }
                        }
                    });
                    parts = newParts;
                });
            }

            let addedStartText = '';
            let addedStartHtml = '';
            let addedEndText = '';
            let addedEndHtml = '';

            [...this.valuesForAddToNamesStart, ...this.valuesForAddToNamesEnd].forEach(item => {
                let valText = '';

                if (item.type === 'filter') {
                    const attributeNames = product.filterItemNamesByFilterUuid[item.value.uuid];
                    if (attributeNames && attributeNames[lang]) {
                        valText = attributeNames[lang];

                        if (item.caseMode) {
                            if (item.caseMode.key === 'upper') {
                                valText = valText.charAt(0).toUpperCase() + valText.slice(1);
                            } else if (item.caseMode.key === 'lower') {
                                valText = valText.charAt(0).toLowerCase() + valText.slice(1);
                            }
                        }
                    }
                } else if (item.type === 'text') {
                    if (item.value && item.value[lang]) {
                        valText = item.value[lang];
                    }
                } else if (item.type === 'category') {
                    valText = product.categoryNames[lang] || '';
                } else if (item.type === 'sku') {
                    valText = product.sku || '';
                }

                if (valText) {
                    if (item.position === 'start') {
                        addedStartText += valText + ' ';
                        addedStartHtml += `<span class="text-success">${valText}</span> `;
                    } else {
                        addedEndText += ' ' + valText;
                        addedEndHtml += ' ' + `<span class="text-success">${valText}</span>`;
                    }
                }
            });

            const hasRemovalChanges = parts.some(p => p.highlight);
            const hasAdditionChanges = addedStartText.length > 0 || addedEndText.length > 0;

            if (!hasRemovalChanges && !hasAdditionChanges) {
                return { hasChanges: false, newName: name, highlightedName: name };
            }
            
            const highlightedNameBody = parts.map(p => p.highlight ? `<span class="text-danger">${p.text}</span>` : p.text).join('');
            const highlightedName = (addedStartHtml + highlightedNameBody + addedEndHtml).replace(/\s+/g, ' ').trim();
            
            let newNameBody = parts.filter(p => !p.highlight).map(p => p.text).join('');
            newNameBody = newNameBody.replace(/\s+/g, ' ').trim();
            const newName = (addedStartText + newNameBody + addedEndText).replace(/\s+/g, ' ').trim();

            return { hasChanges: true, newName, highlightedName };
        },
        someOneProductHasFilter(filter) {
            if (!filter) {
                return false;
            }

            return this.products.some(product => {
                return Boolean(product.filterItemNamesByFilterUuid[filter.uuid])
            });
        },
        isAddButtonDisabled() {
            if (this.addType.key === 'filter') {
                return !this.newAddToNameValue || !this.someOneProductHasFilter(this.newAddToNameValue);
            } else {
                return !this.newAddToNameTextValue;
            }
        },
        async saveNewNames() {
            const updateRequests = this.getUpdateProductRequests(this.products);

            const completed = await this.task.run(async ({setTotal, tick}) => {
                setTotal(updateRequests.length);

                await new HttpSender().sendRequestsInBatches(updateRequests, () => tick());
            });

            if (completed) {
                this.hide();
                this.$emit('names:updated');
            }
        },
        getUpdateProductRequests(products) {
            let requests = [];

            products.forEach(product => {
                let updatedNames = {};

                this.integration.language_aliases.forEach(lang => {
                    const processedName = this.processName(product, lang);
                    if (processedName.hasChanges) {
                        updatedNames[lang] = processedName.newName;
                    }
                });

                if (Object.keys(updatedNames).length > 0) {
                    const request = new HttpRequest(
                        'PATCH',
                        `/integrations/products/${product.uuid}`,
                        {
                            names: updatedNames
                        }
                    );

                    requests.push(request);
                }
            });

            return requests;
        }
    },
    beforeUnmount() {
        this.task.cancel();
    }
}
</script>
<style scoped>
.long-string {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}
.table-image {
    width: 38px;
    height: 38px;
}
.text-overflow {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.sidebar-sticky {
    position: sticky;
    top: 0;
    align-self: flex-start;
}
</style>